import React from "react"
import {useTranslation} from "react-i18next"
import {makeStyles} from "@material-ui/core"

// Components
import ModernDialog from "../../../../../components/modern/ModernDialog"
import ModernButton from "../../../../../components/modern/ModernButton"

// Theme
import {ModernTheme} from "../../../../../theme/modern/tokens"

const LAST_SECONDS = 60;

const useStyles = makeStyles((theme: ModernTheme) => ({
    text: {
        color: theme.modern.ink,
    },
    /**
     * Mono, like every other number in the skin, and in the warn colour: this
     * is a deadline, and the reviewer loses the record when it runs out.
     */
    seconds: {
        font: `600 15px ${theme.modern.mono}`,
        color: theme.modern.warn,
        marginLeft: 4,
    },
}));

type ModernInactivityDialogProps = {
    onClose(): void
    onTimeElapsed(): void
}

/**
 * Modern equivalent of <InactivityWarningDialog/>: same countdown, same
 * one-button answer, drawn in the reading-room skin instead of MUI's.
 *
 * Deliberately NOT dismissable by Escape or by clicking away — `onClose` is
 * passed to the button alone. Answering it is what proves the reviewer is
 * there, so a stray Escape must not do it for them; the record is handed to
 * another doctor when the count reaches zero.
 *
 * @param props
 */
export default function ModernInactivityDialog(props: ModernInactivityDialogProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {onTimeElapsed, onClose} = props;
    const [seconds, setSeconds] = React.useState(LAST_SECONDS);

    React.useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            if (seconds > 0) {
                setSeconds(seconds - 1)
            } else {
                window.clearTimeout(timeoutId);
                onTimeElapsed()
            }
        }, 1000);

        return () => window.clearTimeout(timeoutId)
    }, [seconds, onTimeElapsed]);

    return (
        <ModernDialog
            open={true}
            title={t("records.are_you_still_here")}
            actions={<ModernButton variant="primary" onClick={onClose}>{t("records.i_am_here")}</ModernButton>}
        >
            <span className={styles.text}>{t("records.re_assign_warning_text")}</span>
            <span className={styles.seconds}>{seconds}s</span>
        </ModernDialog>
    )
}
